/**
 * Statistics Tracker
 * Tracks performance metrics and statistics
 */

export class Stats {
  constructor() {
    this.metrics = {
      sessionStart: null,
      sessionDuration: 0,
      framesIn: 0,
      framesOut: 0,
      bytesIn: 0,
      bytesOut: 0,
      dropRate: 0,
      averageLatency: 0,
      reconnects: 0,
      errors: []
    };

    this.latencyMeasurements = [];
    this.maxLatencyMeasurements = 100;
  }

  /**
   * Start session timing
   */
  startSession() {
    this.metrics.sessionStart = Date.now();
  }

  /**
   * Update session duration
   */
  updateSessionDuration() {
    if (this.metrics.sessionStart) {
      this.metrics.sessionDuration = Date.now() - this.metrics.sessionStart;
    }
  }

  /**
   * Record incoming frame
   * @param {number} bytes - Number of bytes
   */
  recordFrameIn(bytes = 640) {
    this.metrics.framesIn++;
    this.metrics.bytesIn += bytes;
  }

  /**
   * Record outgoing frame
   * @param {number} bytes - Number of bytes
   */
  recordFrameOut(bytes = 640) {
    this.metrics.framesOut++;
    this.metrics.bytesOut += bytes;
  }

  /**
   * Record latency measurement
   * @param {number} latency - Latency in milliseconds
   */
  recordLatency(latency) {
    this.latencyMeasurements.push(latency);
    if (this.latencyMeasurements.length > this.maxLatencyMeasurements) {
      this.latencyMeasurements.shift();
    }

    // Calculate average
    const sum = this.latencyMeasurements.reduce((a, b) => a + b, 0);
    this.metrics.averageLatency = Math.round(sum / this.latencyMeasurements.length);
  }

  /**
   * Record reconnection attempt
   */
  recordReconnect() {
    this.metrics.reconnects++;
  }

  /**
   * Record error
   * @param {Error} error - Error object
   */
  recordError(error) {
    this.metrics.errors.push({
      message: error.message,
      timestamp: Date.now()
    });

    // Keep only last 10 errors
    if (this.metrics.errors.length > 10) {
      this.metrics.errors.shift();
    }
  }

  /**
   * Calculate drop rate
   */
  calculateDropRate() {
    const total = this.metrics.framesIn + this.metrics.framesOut;
    if (total === 0) return 0;

    // This is a simplified calculation
    // In a real implementation, you'd track actual drops
    this.metrics.dropRate = 0;
    return this.metrics.dropRate;
  }

  /**
   * Get current statistics snapshot
   * @returns {Object} Statistics
   */
  getSnapshot() {
    this.updateSessionDuration();
    this.calculateDropRate();

    return {
      ...this.metrics,
      sessionDuration: this.metrics.sessionDuration,
      averageLatency: this.metrics.averageLatency,
      dropRate: this.metrics.dropRate
    };
  }

  /**
   * Reset all statistics
   */
  reset() {
    this.metrics = {
      sessionStart: null,
      sessionDuration: 0,
      framesIn: 0,
      framesOut: 0,
      bytesIn: 0,
      bytesOut: 0,
      dropRate: 0,
      averageLatency: 0,
      reconnects: 0,
      errors: []
    };
    this.latencyMeasurements = [];
  }
}
